import React, { useEffect } from 'react';
import Helmet from 'react-helmet';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useHistory } from 'react-router-dom';
import { formatInTimeZone } from 'date-fns-tz';
import { convertTokens } from '@date-fns/upgrade/v2';
import {
  LinearProgress,
  Typography,
  Avatar,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableFooter,
} from '@material-ui/core';

import {
  selectDeedsCreatedByEmployeesForApprove,
  selectUpcomingDeedsLoaded,
} from 'entities/deed/selectors';
import { selectUser } from 'entities/user/selectors';
import Deed from 'entities/deed/model';
import { TableBox } from 'retired/Box';
import PageHeader from 'retired/PageHeader';
import { useInjectEpics } from 'utils/injectEpics';
import { useInjectReducer } from 'utils/injectReducer';
import { Features } from 'utils/features';

import epics from './epics';
import reducer from './reducer';
import { initAction } from './types';
import { selectError } from './selectors';

const styles = {
  nameLink: {
    width: 110,
    display: 'block',
    lineHeight: '32px',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
  },
};

const onClickHandler = (e) => e.stopPropagation();

const ApproveEmployeeDeeds: React.ReactElement<{}> = () => {
  const dispatch = useDispatch();
  useInjectEpics({ key: 'approveEmployeeDeeds', epics });
  useInjectReducer({ key: 'approveEmployeeDeeds', reducer });
  const history = useHistory();

  const user = useSelector(selectUser);
  const deeds = useSelector(selectDeedsCreatedByEmployeesForApprove);
  const loaded = useSelector(selectUpcomingDeedsLoaded);
  const error = useSelector(selectError);

  if (user && !user.hasFeature(Features.USER_DRAFT_DEED)) {
    history.replace('/');
  }

  useEffect(() => {
    dispatch(initAction());
  }, []);

  return (
    <>
      <Helmet title="Employee Submitted Deeds" />
      <PageHeader
        title="Approve Deeds created by your employees"
        subtitle="Your employees submitted deeds for approval. You may view them in detail below and decide whether to approve or deny them."
        onArrowClick={() => history.push('/deeds/volunteer')}
      />
      {!loaded && <LinearProgress />}
      <TableBox>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell size="medium">Employee</TableCell>
              <TableCell size="medium">Submitted on</TableCell>
              <TableCell size="medium">Type</TableCell>
              <TableCell size="medium">Title</TableCell>
              <TableCell size="medium">Nonprofit</TableCell>
              <TableCell size="medium">Preview</TableCell>
              <TableCell size="medium">Review / Publish</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loaded && error && (
              <TableRow>
                <TableCell colspan={7}>
                  <Typography>There was en error loading Deeds list</Typography>
                </TableCell>
              </TableRow>
            )}
            {!deeds.size && loaded && !error && (
              <TableRow>
                <TableCell colspan={7}>
                  <Typography>You have no Deeds to review!</Typography>
                </TableCell>
              </TableRow>
            )}
            {!!deeds.size &&
              deeds.toArray().map((deed: Deed) => (
                <TableRow key={deed.id}>
                  <TableCell>
                    <Avatar
                      alt={`${deed.submitter.get('fullName').get('first')} ${deed.submitter
                        .get('fullName')
                        .get('last')}`}
                      src={deed.submitter.mainPicture?.replace('http://', 'https://')}
                      style={{
                        width: 32,
                        height: 32,
                        float: 'left',
                        marginRight: 10,
                      }}
                    />
                    <Link
                      to={`/volunteers/${deed.submitter.id}`}
                      className="hoverable"
                      style={styles.nameLink}
                      onClick={onClickHandler}
                    >
                      {deed.submitter.get('fullName').get('first')}{' '}
                      {deed.submitter.get('fullName').get('last')}
                    </Link>
                  </TableCell>
                  <TableCell>
                    {formatInTimeZone(deed.createdAt, deed.timeZone, convertTokens('MMM DD YYYY'))}
                  </TableCell>
                  <TableCell>{deed.type === 'Campaign' ? 'Fundraiser' : deed.type}</TableCell>
                  <TableCell>{deed.name}</TableCell>
                  <TableCell>
                    {deed.organization?.get('name') ||
                      deed.nonprofits?.map((nonprofit) => nonprofit.get('name'))?.join(', ')}
                  </TableCell>
                  <TableCell>
                    <Link to={`/deeds/${deed.pathSlug}/${deed.id}`}>Preview</Link>
                  </TableCell>
                  <TableCell>
                    <Link to={`/deeds/${deed.pathSlug}/edit/${deed.id}`}>Review / Publish</Link>
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell
                size="medium"
                colSpan={100}
                style={{ color: '#0000008a', fontSize: '0.875rem' }}
              >
                Total: {deeds.size}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </TableBox>
    </>
  );
};

export default ApproveEmployeeDeeds;
